import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Poster from 'brastrap/editorial/poster/Poster';
import EditorialContainer from 'brastrap/editorial/editorial-container/EditorialContainer';
import ResultsDisplay from 'brastrap/promotional/results-display/ResultsDisplay';
import ResultsList from 'brastrap/promotional/results-list/ResultsList';
import Pagination from 'brastrap/containers/pagination/PaginationContainer';
import ResultsFacets from 'brastrap/promotional/results-facets/ResultsFacets';
import Link from 'brastrap/containers/link/LinkContainer';

const Results = props => {
  const displayResults = !!props.items.length && !props.hideResults;
  const displayPoster = !!props.poster && props.poster.title;
  const displayResultsTitle = !props.poster && props.searchPhrase;
  const displaySuggestion = !!props.suggestion;
  const {
    containers: editorialContainers,
    subcollections,
    displaySizeSelectors,
  } = props;
  const containers = [...editorialContainers];
  const resultsContainerProps = {};

  if (subcollections.length) {
    // Show above other containers if no results found
    containers[displayResults || props.hideResults ? 'push' : 'unshift']({
      type: 'Block',
      id: 'subcollections',
      rows: [
        {
          items: subcollections.map(sc => ({
            _type: 'imageButton',
            id: sc.reference,
            image: sc.image,
            selected: sc.reference === props.filter.subcollection,
            className: 'c-results__subcollection',
            modifiers: '2up',
            onSelect: (subcollection, selected) => {
              props.onFilter({
                subcollection: selected ? subcollection : undefined,
              });
            },
            action: {
              label: sc.label,
              modifiers: ['minor-hollow'],
            },
          })),
        },
      ],
    });
  }

  // TODO: messages here need localising if we are not moving this to contentful
  if (displaySizeSelectors) {
    // Show above other containers if no results found
    containers[displayResults || props.hideResults ? 'push' : 'unshift']({
      type: 'SizesSelector',
      id: 'sizes-selector',
      ...props.sizeFacetData,
      submitSelectedFacets: props.onReceiveSizeFacets,
      containerProps: {
        actions: [
          {
            label: 'Search',
            modifiers: ['large'],
            submit: true,
          },
        ],
      },
      braSizesTitle: [
        {
          content: 'SELECT YOUR BRA SIZE',
          style: 'drawn',
          size: '2',
          color: 'red',
        },
      ],
      clothingSizesTitle: [
        {
          content: 'SELECT YOUR CLOTHING SIZE',
          style: 'drawn',
          size: '2',
          color: 'red',
        },
      ],
    });
  }

  const displayContainers = !!containers && containers.length > 0;

  if (props.resultsContainerId) {
    resultsContainerProps.id = props.resultsContainerId;
  }

  return (
    <section
      className={`c-results${displayResults ? '' : ' c-results--empty'}`}
    >
      {displayPoster && <Poster {...props.poster} />}

      {displayResultsTitle && (
        <header className="c-results__header">
          <h1 className="c-results__title">
            {displayResults ? (
              <FormattedMessage
                id="results.title"
                defaultMessage={`Showing {total} {total, plural,
                one {result}
                other {results}
              } for "{searchPhrase}"`}
                values={{
                  searchPhrase: <b>{props.searchPhrase}</b>,
                  total: props.total,
                }}
              />
            ) : (
              <FormattedMessage
                id="results.no-results"
                defaultMessage={`No results found for
                "{searchPhrase}"`}
                values={{ searchPhrase: <b>{props.searchPhrase}</b> }}
              />
            )}
          </h1>

          <div className="c-results__desc">
            {displaySuggestion ? (
              <FormattedMessage
                id="results.suggestion"
                defaultMessage={'Did you mean {suggestion}?'}
                values={{
                  suggestion: (
                    <Link href={props.suggestionUrl} title={props.suggestion}>
                      {props.suggestion}
                    </Link>
                  ),
                }}
              />
            ) : (
              props.desc
            )}
          </div>
        </header>
      )}

      {displayContainers && <EditorialContainer containers={containers} />}

      {displayResults && (
        <div className="c-results__main" {...resultsContainerProps}>
          {props.facets.length > 0 && (
            <ResultsFacets
              facets={props.facets}
              isOpen={props.isOpen}
              location={props.location}
              modalId={props.modalId}
              onChange={props.onFacetChange}
              onModalClose={props.onModalClose}
              onModalOpen={props.onModalOpen}
              showInModal={props.showFacetsInModal}
            />
          )}

          <ResultsDisplay
            {...props.filter}
            onChange={props.onFilter}
            location={props.location}
            total={props.total}
          />

          <ResultsList
            currency={props.currency}
            isFetching={props.isFetching}
            onStyleColourClick={props.onStyleColourClick}
            onStyleColourChange={props.onStyleColourChange}
            productsOrContentModules={props.items}
            onSaveItemPress={props.onSaveItemPress}
            savedItemsMap={props.savedItemsMap}
            collection={props.collection}
            pathname={props.pathname}
          />

          <Pagination
            {...props.pagination}
            onClick={props.onPaginate}
            location={props.location}
          />
        </div>
      )}
    </section>
  );
};

Results.propTypes = {
  currency: PropTypes.string,
  desc: PropTypes.string,
  pageTitle: PropTypes.string,
  filter: PropTypes.shape(ResultsDisplay.propTypes).isRequired,
  isFetching: PropTypes.bool,
  items: PropTypes.array.isRequired,
  location: PropTypes.object,
  onStyleColourChange: PropTypes.func,
  onStyleColourClick: PropTypes.func,
  onFacetChange: PropTypes.func,
  onReceiveSizeFacets: PropTypes.func,
  onFilter: PropTypes.func,
  onPaginate: PropTypes.func,
  pagination: PropTypes.shape(Pagination.propTypes).isRequired,
  poster: PropTypes.shape(Poster.propTypes),
  total: PropTypes.number,
  suggestion: PropTypes.string,
  suggestionUrl: PropTypes.string,
  searchPhrase: PropTypes.string,
  subcollections: PropTypes.array,
  facets: PropTypes.array,
  containers: PropTypes.array,
  hideResults: PropTypes.bool,
  showFacetsInModal: PropTypes.bool,
  modalId: PropTypes.string,
  isOpen: PropTypes.bool,
  onModalOpen: PropTypes.func,
  onModalClose: PropTypes.func,
  onSaveItemPress: PropTypes.func,
  resultsContainerId: PropTypes.string,
  sizeFacetData: PropTypes.object,
  displaySizeSelectors: PropTypes.bool,
  contentModules: PropTypes.array,
  savedItemsMap: PropTypes.object,
  collection: PropTypes.string,
  pathname: PropTypes.string,
};

Results.defaultProps = {
  items: [],
  onFilter: () => {},
  onPaginate: () => {},
  onStyleColourChange: () => {},
  onReceiveSizeFacets: () => {},
  onModalOpen: () => {},
  onModalClose: () => {},
  onSaveItemPress: () => {},
  savedItemsMap: {},
  suggestionUrl: '#',
  containers: [],
  subcollections: [],
  facets: [],
  pageTitle: '',
  sizeFacetData: {},
};

export default Results;
